/**
 * 素材サムネイルの共通表示。タイル・リスト・スライドショーの3ビューで使う。
 *
 * 【種別アイコン】
 * 動画か写真かをサムネイル右下の小さなアイコンで示す。一覧では拡張子が
 * 見えないことが多く、静止画サムネイルだけでは動画と写真を見分けられない。
 *
 * 【縦長画像の見せ方】
 * 枠は 16:9 で統一しているため、縦長・正方形の画像を cover で切ると
 * 中央の帯しか見えず内容が分からない。枠との縦横比の差が大きいものだけ
 * contain に切り替え、余白は同じ画像をぼかした背景で埋める
 * （黒帯より内容の色味が伝わり、一覧に穴が開いたように見えない）。
 *
 * 縦横比は assets.width/height（メタデータ抽出済みなら入っている）を使い、
 * 無ければ画像の読み込み完了時に naturalWidth/naturalHeight から判定する。
 * 判定前は cover で出す（一瞬の切り替わりはあるが、何も出さないより良い）。
 */
import { useState, type ReactNode } from 'react'
import type { Asset } from '../lib/supabase.ts'
import { KIND_LABEL } from '../lib/format.ts'

export function KindIcon({ kind }: { kind: string }) {
  if (kind === 'video') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M5 3.9v8.2c0 .7.76 1.13 1.36.77l6.8-4.1a.9.9 0 0 0 0-1.54l-6.8-4.1A.9.9 0 0 0 5 3.9z" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2.2 12.6 5.9 7.5a.6.6 0 0 1 .96-.01l1.92 2.5 1.3-1.7a.6.6 0 0 1 .95 0l2.77 3.62c.3.4.02.95-.48.95H2.68a.6.6 0 0 1-.48-.96z" />
      <circle cx="11.2" cy="4.4" r="1.6" />
    </svg>
  )
}

/** 16:9 の枠に対して、cover で切ると内容が大きく欠ける縦横比か。 */
const needsContain = (w: number, h: number): boolean => {
  const aspect = w / h
  // 1.15 未満: 正方形〜縦長（cover だと上下が大きく欠ける）
  // 3.2 超: 極端なパノラマ（cover だと左右が大きく欠ける）
  return aspect < 1.15 || aspect > 3.2
}

export function AssetThumb({
  asset,
  src,
  broken,
  placeholder,
  onError,
  children,
}: {
  asset: Asset
  src: string | null
  broken?: boolean
  /** サムネイルが出せないときの文言。 */
  placeholder?: ReactNode
  /** 画像URLは有効なのに実体が無い（R2 から消えている）ときに呼ぶ。 */
  onError?: () => void
  /** 状態バッジなど、上に重ねたいもの。 */
  children?: ReactNode
}) {
  // メタデータ未抽出の素材のために、実際に読み込んだ画像の寸法を控えておく
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const w = asset.width ?? natural?.w ?? null
  const h = asset.height ?? natural?.h ?? null
  const contain = w != null && h != null && h > 0 && needsContain(w, h)

  const showImg = !!src && !broken
  const kindLabel = KIND_LABEL[asset.kind] ?? asset.kind

  return (
    <div className={`thumb${contain && showImg ? ' fit-contain' : ''}`}>
      {showImg ? (
        <>
          {/* 余白を埋めるぼかし背景。同じURLなのでリクエストは増えない */}
          {contain && <img className="thumb-bg" src={src} alt="" aria-hidden="true" />}
          <img
            className="thumb-img"
            src={src}
            alt=""
            loading="lazy"
            onLoad={(e) => {
              if (asset.width != null && asset.height != null) return
              const el = e.currentTarget
              if (!natural && el.naturalWidth > 0 && el.naturalHeight > 0) {
                setNatural({ w: el.naturalWidth, h: el.naturalHeight })
              }
            }}
            onError={onError}
          />
        </>
      ) : (
        <span className="thumb-placeholder">{placeholder ?? 'サムネイル未生成'}</span>
      )}
      <span className="kind-badge" title={kindLabel} aria-label={kindLabel}>
        <KindIcon kind={asset.kind} />
      </span>
      {children}
    </div>
  )
}
